import { Search, Users } from 'lucide-react'
import { useMemo, useState } from 'react'
import useSWR from 'swr'

import { EmptyState } from '@/components/EmptyState'
import { PageHeader, SegmentedTabs, StatStrip } from '@/components/ds'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { AdminUsersTable } from '@/components/UsersTable/AdminUsersTable'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { Collection } from '@/interfaces/Collection'
import { AdminUser } from '@/types/User'

type Filter = 'all' | 'unverified' | 'admin' | 'noAccount'

const Count = ({ value }: { value: number }) => (
  <span className="text-2xl font-medium tracking-[-0.02em] tabular">{value.toLocaleString('cs-CZ')}</span>
)

const matches = (user: AdminUser, filter: Filter) => {
  if (filter === 'unverified') return !user.verified
  if (filter === 'admin') return user.roles.includes('admin')
  if (filter === 'noAccount') return user.accounts.length === 0
  return true
}

export default function AdminUsersPage() {
  const [filter, setFilter] = useState<Filter>('all')
  const [query, setQuery] = useState('')

  const { data: users, isLoading, mutate } = useSWR<Collection<AdminUser>>('/api/v2/admin/users', apiFetcher)

  useSetPageHeader({
    breadcrumbs: [{ href: '/admin/users', children: 'Uživatelé' }],
  })

  const all = useMemo(() => users?.data ?? [], [users])

  const counts = useMemo(
    () => ({
      all: all.length,
      unverified: all.filter((u) => matches(u, 'unverified')).length,
      admin: all.filter((u) => matches(u, 'admin')).length,
      noAccount: all.filter((u) => matches(u, 'noAccount')).length,
    }),
    [all],
  )

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return all
      .filter((u) => matches(u, filter))
      .filter(
        (u) =>
          !q ||
          [u.fullName ?? '', u.email, String(u.id), ...u.accounts.map((a) => a.accountName ?? '')].some((v) => v.toLowerCase().includes(q)),
      )
  }, [all, filter, query])

  const toggle = (value: Filter) => setFilter((f) => (f === value ? 'all' : value))

  return (
    <>
      <title>Uživatelé</title>
      <div className="mx-auto w-full max-w-[1280px]">
        <PageHeader title="Uživatelé" description={users ? `${all.length} uživatelů napříč účty` : 'Načítám'} />

        <StatStrip
          className="mb-6"
          items={[
            { label: 'Uživatelé', value: <Count value={counts.all} />, hint: 'celkem', active: filter === 'all', onClick: () => setFilter('all') },
            { label: 'Nepotvrzení', value: <Count value={counts.unverified} />, hint: 'bez ověřeného e-mailu', active: filter === 'unverified', onClick: () => toggle('unverified') },
            { label: 'Bez účtu', value: <Count value={counts.noAccount} />, hint: 'nepatří k žádnému účtu', active: filter === 'noAccount', onClick: () => toggle('noAccount') },
            { label: 'Administrátoři', value: <Count value={counts.admin} />, hint: 'přístup do administrace', active: filter === 'admin', onClick: () => toggle('admin') },
          ]}
        />

        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <SegmentedTabs<Filter>
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'Vše', count: counts.all },
              { value: 'unverified', label: 'Nepotvrzení', count: counts.unverified },
              ...(counts.noAccount ? [{ value: 'noAccount' as const, label: 'Bez účtu', count: counts.noAccount }] : []),
              { value: 'admin', label: 'Administrátoři', count: counts.admin },
            ]}
          />
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Jméno, e-mail nebo účet" className="pl-8" />
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border">
          {isLoading && (
            <div className="divide-y">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex h-14 items-center gap-6 px-3">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="ml-auto h-4 w-20" />
                </div>
              ))}
            </div>
          )}

          {!isLoading && visible.length > 0 && <AdminUsersTable users={visible} refresh={() => mutate()} />}

          {!isLoading && visible.length === 0 && (
            <div className="p-6">
              <EmptyState icon={Users}
                title={all.length ? 'Žádný uživatel neodpovídá filtru' : 'Zatím tu nejsou žádní uživatelé'}
                description={all.length ? 'Zkuste jiný filtr nebo hledaný výraz.' : undefined}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
